import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useServerTable } from '@/hooks/use-server-table';
import { mergeWhere } from '@/lib/table-query';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui-kit/tabs';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui-kit/toggle-group';
import { useUrlChoice, useUrlTab } from '@/hooks/use-linked-record';
import { ASSESSMENT_TYPES, AssessmentTypeKey } from '@/modules/shared/hr-form-engine';
import {
  useDepartmentOptions,
  useDepartmentScope,
  useDesignationOptions,
  useEmployeesByUserIds,
} from '@/modules/shared/hr-lookups';
import {
  buildReviewFilter,
  DEFAULT_PM_REVIEWS_ORDER,
  EMPTY_REVIEW_FILTERS,
  PmReview,
  ReviewFilters,
  ReviewFilterValue,
  useGetPmReviews,
} from '@/modules/performance/hr-review-core';
import AdvanceDataTable from '@/components/core/advance-data-table/advance-data-table';
import { createReviewsTableColumns } from '../../components/reviews-table/reviews-table-columns';
import { PageHeader } from '@/components/core';
import { appPath } from '@/constant/app-paths';
import { useReviewTypeCounts } from '../../hooks/use-review-type-counts';
import {
  createdInMonthClause,
  currentOrgMonth,
  formatReviewMonth,
  monthsAround,
  ReviewMonth,
} from '../../utils/review-months';

/** The URL param of the month strip. */
const MONTH_PARAM = 'month';

const AssessmentTypeTab = ({
  assessmentType,
  month,
}: {
  assessmentType: AssessmentTypeKey;
  month: ReviewMonth;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pagination, resetPage, order, columnWhere, tableProps } = useServerTable({
    pageSize: 15,
    defaultOrder: DEFAULT_PM_REVIEWS_ORDER,
  });
  const [filters, setFilters] = useState<ReviewFilterValue>(EMPTY_REVIEW_FILTERS);
  // A new month starts on the first page; the tab's own filters stay.
  useEffect(() => {
    resetPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only when the month changes
  }, [month]);

  const { data: departments = [] } = useDepartmentOptions();
  const { getDepartmentIdCondition, isLoading: isScopeLoading } = useDepartmentScope();
  const { data: designations = [] } = useDesignationOptions();
  const departmentNames = useMemo(
    () => new Map(departments.map((option) => [option.ItemId, option.Title])),
    [departments]
  );
  const designationNames = useMemo(
    () => new Map(designations.map((option) => [option.ItemId, option.Title])),
    [designations]
  );

  const { data, isLoading, error } = useGetPmReviews({
    pageNo: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    filter: mergeWhere(
      buildReviewFilter(
        filters,
        { AssessmentType: assessmentType, ...createdInMonthClause(month) },
        getDepartmentIdCondition(filters.departmentId)
      ),
      columnWhere
    ),
    order,
  });
  const { byUserId: employeesByUserId } = useEmployeesByUserIds(
    (data?.items ?? []).map((review) => review.ReviewForId)
  );
  const emailOf = (userId: string) => employeesByUserId.get(userId)?.Email;

  const handleFiltersChange = (next: ReviewFilterValue) => {
    setFilters(next);
    resetPage();
  };

  const columns = createReviewsTableColumns({ t, departmentNames, designationNames, emailOf });

  return (
    <div className="flex flex-col gap-4">
      <ReviewFilters value={filters} onChange={handleFiltersChange} />
      <AdvanceDataTable
        {...tableProps}
        data={data?.items ?? []}
        columns={columns}
        onRowClick={(review: PmReview) => navigate(appPath.review(review.ItemId))}
        // A department-limited user's list is only right once their departments are known.
        isLoading={isLoading || isScopeLoading}
        error={error instanceof Error ? error : null}
        pagination={{
          pageIndex: pagination.pageIndex,
          pageSize: pagination.pageSize,
          totalCount: data?.totalCount ?? 0,
        }}
        isExpandRowContent={false}
        mobileView="cards"
      />
    </div>
  );
};

const TYPE_KEYS = ASSESSMENT_TYPES.map((option) => option.key);

/**
 * Performance › All Reviews (prototype `all-reviews`): a strip of 13 months (6 before the organization's
 * current month, it, 6 after) picks whose reviews were set up that month; one tab row per review type, each
 * with its count for the month. Month and type live in the URL (`?month=YYYY-MM&tab=`).
 */
export const ReviewsListPage = () => {
  const { t, i18n } = useTranslation();
  const thisMonth = useMemo(() => currentOrgMonth(), []);
  const months = useMemo(() => monthsAround(thisMonth), [thisMonth]);
  const [month, setMonth] = useUrlChoice(MONTH_PARAM, months, thisMonth);
  const [tab, setTab] = useUrlTab(TYPE_KEYS, TYPE_KEYS[0]);
  const { data: counts } = useReviewTypeCounts(month);

  return (
    <div className="flex w-full flex-col gap-4">
      <PageHeader title={t('ALL_REVIEWS')} description={t('ALL_REVIEWS_DESCRIPTION')} />

      {/* Scrolls sideways on narrow screens instead of wrapping. */}
      <div className="-mx-1 overflow-x-auto px-1 pb-1">
        <ToggleGroup
          type="single"
          variant="outline"
          size="sm"
          value={month}
          onValueChange={(next) => next && setMonth(next)}
          aria-label={t('REVIEW_MONTH')}
          className="w-max"
        >
          {months.map((option) => (
            <ToggleGroupItem key={option} value={option} className="whitespace-nowrap px-2.5">
              {formatReviewMonth(option, i18n.language)}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="max-sm:w-full max-sm:justify-start max-sm:overflow-x-auto">
          {ASSESSMENT_TYPES.map((option) => (
            <TabsTrigger key={option.key} value={option.key} count={counts?.[option.key]}>
              {t(option.title)}
            </TabsTrigger>
          ))}
        </TabsList>
        {ASSESSMENT_TYPES.map((option) => (
          <TabsContent key={option.key} value={option.key}>
            <AssessmentTypeTab assessmentType={option.key} month={month} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};
